import { action, computed, makeObservable, observable } from "mobx";
import SwayyShipmentAPI from "../api/SwayyShipmentAPI";
import {
  GetSwayyShipmentsParams,
  SwayyCourierPartner,
  SwayyShipment,
  SwayyShipmentFullDetail,
  SwayyShipmentsPagination,
} from "../../../models/SwayyShipment/SwayyShipment";
import {
  SWAYY_SHIPMENT_DEFAULT_STATUS_TAB,
  SWAYY_SHIPMENT_STATUS_ALL_TAB,
  SWAYY_SHIPMENT_STATUS_OPTIONS,
} from "../../../models/SwayyShipment/SwayyShipmentFilters";
import { swayyCarrierStore } from "./SwayyCarrierStore";
import { swayyWarehouseStore } from "./SwayyWarehouseStore";

const parseStatusParam = (value: string | null): string => {
  if (!value || value === SWAYY_SHIPMENT_STATUS_ALL_TAB) return "";
  const statusIds = value.split(",").filter(Boolean);
  if (statusIds.length !== 1) return "";
  const isKnown = SWAYY_SHIPMENT_STATUS_OPTIONS.some(
    (option) => option.id === statusIds[0],
  );
  return isKnown ? statusIds[0] : "";
};

export const DEFAULT_SWAYY_SHIPMENT_LIST_PARAMS: GetSwayyShipmentsParams = {
  page: 1,
  limit: 20,
  status: SWAYY_SHIPMENT_DEFAULT_STATUS_TAB,
  carrier_id: "",
  warehouse_id: "",
  awb: "",
  from_date: "",
  to_date: "",
  overdue: "",
};

export class SwayyShipmentStore {
  swayyShipmentAPI: SwayyShipmentAPI;
  shipments: SwayyShipment[];
  pagination: SwayyShipmentsPagination;
  params: GetSwayyShipmentsParams;
  filtersOpen: boolean;
  draftStatus: string[];
  draftCarrierId: string[];
  draftWarehouseId: string[];
  draftAwb: string;
  draftFromDate: string;
  draftToDate: string;
  draftOverdue: string[];
  loading: boolean;
  lookupsLoading: boolean;
  error: string | null;
  detail: SwayyShipmentFullDetail | null;
  detailLoading: boolean;
  detailOpen: boolean;
  csvDownloading: boolean;
  csvError: string | null;
  private fetchGeneration: number;

  constructor() {
    this.swayyShipmentAPI = new SwayyShipmentAPI();
    this.shipments = [];
    this.pagination = { total: 0, page: 1, limit: 20 };
    this.params = { ...DEFAULT_SWAYY_SHIPMENT_LIST_PARAMS };
    this.filtersOpen = false;
    this.draftStatus = this.params.status ? [this.params.status] : [];
    this.draftCarrierId = [];
    this.draftWarehouseId = [];
    this.draftAwb = "";
    this.draftFromDate = "";
    this.draftToDate = "";
    this.draftOverdue = [];
    this.loading = false;
    this.lookupsLoading = false;
    this.error = null;
    this.detail = null;
    this.detailLoading = false;
    this.detailOpen = false;
    this.csvDownloading = false;
    this.csvError = null;
    this.fetchGeneration = 0;

    makeObservable(this, {
      shipments: observable,
      pagination: observable,
      params: observable,
      filtersOpen: observable,
      draftStatus: observable,
      draftCarrierId: observable,
      draftWarehouseId: observable,
      draftAwb: observable,
      draftFromDate: observable,
      draftToDate: observable,
      draftOverdue: observable,
      loading: observable,
      lookupsLoading: observable,
      error: observable,
      detail: observable,
      detailLoading: observable,
      detailOpen: observable,
      csvDownloading: observable,
      csvError: observable,
      statusTab: computed,
      getShipments: action,
      loadLookups: action,
      getShipmentDetail: action,
      downloadCourierCsv: action,
      clearCsvError: action,
      closeDetail: action,
      setStatusTab: action,
      toggleFiltersOpen: action,
      setDraftStatus: action,
      setDraftCarrierId: action,
      setDraftWarehouseId: action,
      setDraftAwb: action,
      setDraftDateRange: action,
      setDraftOverdue: action,
      applyFilters: action,
      clearFilters: action,
      setParams: action,
      handlePageChange: action,
      handleRowsPerPageChange: action,
      initializeParamsFromSearch: action,
      buildSearchParams: action,
      reset: action,
    });
  }

  get statusTab(): string {
    const status = this.params.status || "";
    if (!status) return SWAYY_SHIPMENT_STATUS_ALL_TAB;
    const isKnown = SWAYY_SHIPMENT_STATUS_OPTIONS.some(
      (option) => option.id === status,
    );
    return isKnown ? status : SWAYY_SHIPMENT_STATUS_ALL_TAB;
  }

  getShipments = async (params: GetSwayyShipmentsParams = this.params) => {
    const generation = ++this.fetchGeneration;
    this.loading = true;
    this.error = null;
    try {
      const response = await this.swayyShipmentAPI.getShipments(params);
      if (generation !== this.fetchGeneration) return null;
      this.shipments = response.shipments;
      this.pagination = response.pagination;
      return response;
    } catch (error: any) {
      if (generation !== this.fetchGeneration) return null;
      this.error = error?.message || "Failed to fetch shipments";
      throw error;
    } finally {
      if (generation === this.fetchGeneration) {
        this.loading = false;
      }
    }
  };

  loadLookups = async () => {
    this.lookupsLoading = true;
    try {
      await Promise.all([
        swayyWarehouseStore.getWarehouses(),
        swayyCarrierStore.getCarriers(),
      ]);
    } catch (error: any) {
      throw error;
    } finally {
      this.lookupsLoading = false;
    }
  };

  getShipmentDetail = async (shipmentId: number | string) => {
    this.detailLoading = true;
    this.detailOpen = true;
    try {
      const detail = await this.swayyShipmentAPI.getShipment(shipmentId);
      this.detail = detail;
      return detail;
    } catch (error: any) {
      this.detail = null;
      throw error;
    } finally {
      this.detailLoading = false;
    }
  };

  clearCsvError = () => {
    this.csvError = null;
  };

  downloadCourierCsv = async (courier: SwayyCourierPartner): Promise<void> => {
    this.csvDownloading = true;
    this.csvError = null;
    try {
      const { blob, filename } = await this.swayyShipmentAPI.downloadCourierCsv(
        courier,
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      this.csvError = error?.message || "Failed to download courier template";
      throw error;
    } finally {
      this.csvDownloading = false;
    }
  };

  closeDetail = () => {
    this.detailOpen = false;
    this.detail = null;
    this.detailLoading = false;
  };

  setStatusTab = (statusId: string) => {
    const status =
      !statusId || statusId === SWAYY_SHIPMENT_STATUS_ALL_TAB ? "" : statusId;
    if ((this.params.status || "") === status) return;

    this.shipments = [];
    this.pagination = { ...this.pagination, total: 0, page: 1 };
    this.draftStatus = status ? [status] : [];
    this.params = {
      ...this.params,
      status,
      page: 1,
    };
  };

  initializeParamsFromSearch = (searchParams: URLSearchParams) => {
    const page = Math.max(
      1,
      parseInt(searchParams.get("page") || "1", 10) || 1,
    );
    const parsedLimit = parseInt(searchParams.get("limit") || "20", 10);
    const limit = [10, 20, 50, 100].includes(parsedLimit) ? parsedLimit : 20;
    const rawStatus = searchParams.get("status");
    const status =
      rawStatus === null
        ? SWAYY_SHIPMENT_DEFAULT_STATUS_TAB
        : parseStatusParam(rawStatus);
    const carrierId = searchParams.get("carrier_id") || "";
    const warehouseId = searchParams.get("warehouse_id") || "";
    const awb = searchParams.get("awb") || "";
    const fromDate = searchParams.get("from_date") || "";
    const toDate = searchParams.get("to_date") || "";
    const overdueParam = searchParams.get("overdue");
    const overdue: boolean | "" =
      overdueParam === "true" ? true : overdueParam === "false" ? false : "";

    this.params = {
      page,
      limit,
      status,
      carrier_id: carrierId,
      warehouse_id: warehouseId,
      awb,
      from_date: fromDate,
      to_date: toDate,
      overdue,
    };
    this.draftStatus = status ? [status] : [];
    this.draftCarrierId = carrierId ? [carrierId] : [];
    this.draftWarehouseId = warehouseId ? [warehouseId] : [];
    this.draftAwb = awb;
    this.draftFromDate = fromDate.slice(0, 10);
    this.draftToDate = toDate.slice(0, 10);
    this.draftOverdue =
      overdue === true ? ["true"] : overdue === false ? ["false"] : [];
  };

  buildSearchParams = (): URLSearchParams => {
    const searchParams = new URLSearchParams();
    const { params } = this;

    if (params.page > 1) searchParams.set("page", String(params.page));
    if (params.limit && params.limit !== 20) {
      searchParams.set("limit", String(params.limit));
    }
    searchParams.set(
      "status",
      params.status || SWAYY_SHIPMENT_STATUS_ALL_TAB,
    );
    if (params.carrier_id) {
      searchParams.set("carrier_id", String(params.carrier_id));
    }
    if (params.warehouse_id) {
      searchParams.set("warehouse_id", String(params.warehouse_id));
    }
    if (params.awb) searchParams.set("awb", params.awb);
    if (params.from_date) searchParams.set("from_date", params.from_date);
    if (params.to_date) searchParams.set("to_date", params.to_date);
    if (params.overdue === true) searchParams.set("overdue", "true");
    if (params.overdue === false) searchParams.set("overdue", "false");

    return searchParams;
  };

  toggleFiltersOpen = () => {
    this.filtersOpen = !this.filtersOpen;
  };

  setDraftStatus = (status: string[]) => {
    this.draftStatus = status;
  };

  setDraftCarrierId = (carrierId: string[]) => {
    this.draftCarrierId = carrierId;
  };

  setDraftWarehouseId = (warehouseId: string[]) => {
    this.draftWarehouseId = warehouseId;
  };

  setDraftAwb = (awb: string) => {
    this.draftAwb = awb;
  };

  setDraftDateRange = (fromDate: string, toDate: string) => {
    this.draftFromDate = fromDate;
    this.draftToDate = toDate;
  };

  setDraftOverdue = (overdue: string[]) => {
    this.draftOverdue = overdue;
  };

  applyFilters = () => {
    const overdueValue = this.draftOverdue[0];
    const fromDate = this.draftFromDate
      ? `${this.draftFromDate} 00:00:00`
      : "";
    const toDate = this.draftToDate ? `${this.draftToDate} 23:59:59` : "";
    this.params = {
      ...this.params,
      carrier_id: this.draftCarrierId[0] || "",
      warehouse_id: this.draftWarehouseId[0] || "",
      awb: this.draftAwb.trim(),
      from_date: fromDate,
      to_date: toDate,
      overdue:
        overdueValue === "true"
          ? true
          : overdueValue === "false"
            ? false
            : "",
      page: 1,
    };
  };

  clearFilters = () => {
    this.draftCarrierId = [];
    this.draftWarehouseId = [];
    this.draftAwb = "";
    this.draftFromDate = "";
    this.draftToDate = "";
    this.draftOverdue = [];
    this.params = {
      ...DEFAULT_SWAYY_SHIPMENT_LIST_PARAMS,
      limit: this.params.limit,
      status: this.params.status,
    };
  };

  setParams = (partial: Partial<GetSwayyShipmentsParams>) => {
    this.params = { ...this.params, ...partial };
  };

  handlePageChange = (page: number) => {
    this.params = { ...this.params, page };
  };

  handleRowsPerPageChange = (limit: number) => {
    this.params = { ...this.params, limit, page: 1 };
  };

  reset = () => {
    this.shipments = [];
    this.pagination = { total: 0, page: 1, limit: 20 };
    this.params = { ...DEFAULT_SWAYY_SHIPMENT_LIST_PARAMS };
    this.filtersOpen = false;
    this.draftStatus = this.params.status ? [this.params.status] : [];
    this.draftCarrierId = [];
    this.draftWarehouseId = [];
    this.draftAwb = "";
    this.draftFromDate = "";
    this.draftToDate = "";
    this.draftOverdue = [];
    this.loading = false;
    this.error = null;
    this.detail = null;
    this.detailLoading = false;
    this.detailOpen = false;
  };
}

/** Local singleton — do not register in shared stores/index. */
export const swayyShipmentStore = new SwayyShipmentStore();
